import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Eye, TrendingUp, Star, MessageCircle, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function fmtMoney(cents) { return `$${(cents / 100).toFixed(2)}`; }

function KPI({ label, value, hint }) {
  return (
    <div className="border border-[rgba(217,180,114,0.15)] rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-1">{label}</div>
      <div className="font-mono-brass text-2xl text-[#F7F3EC] leading-tight">{value}</div>
      {hint && <div className="text-[10px] text-[#6E675E] font-serif-body italic mt-1">{hint}</div>}
    </div>
  );
}

export function AnalyticsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/analytics/mine").then(({ data }) => setData(data)).catch(() => setData({ rows: [], totals: null }));
  }, []);
  if (!data) return null;

  return (
    <section className="mb-14" data-testid="analytics-panel">
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="w-5 h-5 text-[#D9B472]" />
        <h2 className="font-serif-display text-2xl sm:text-3xl text-[#F7F3EC]">Insights</h2>
      </div>
      <p className="text-sm text-[#A8A095] font-serif-body italic mb-6">What's landing — views, unlocks, ratings, and one-line notes across every listing.</p>

      {data.totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <KPI label="Listings" value={data.totals.listings} />
          <KPI label="Total views" value={data.totals.views.toLocaleString()} />
          <KPI label="Unlocks" value={data.totals.purchases} />
          <KPI label="Revenue" value={fmtMoney(data.totals.revenue_cents)} hint="Sales + tips" />
          <KPI label="Avg rating" value={data.totals.rating_count ? `${data.totals.avg_rating.toFixed(1)}★` : "—"} hint={data.totals.rating_count ? `${data.totals.rating_count} rating${data.totals.rating_count === 1 ? "" : "s"}` : "no ratings yet"} />
          <KPI label="Notes" value={data.totals.reactions} hint="Reader notes" />
        </div>
      )}

      {data.rows.length === 0 ? (
        <p className="text-[#6E675E] font-serif-body italic">Nothing listed yet — publish a fragment or character and this table lights up.</p>
      ) : (
        <div className="overflow-x-auto gold-frame paper-scroll rounded-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(217,180,114,0.15)] text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">
                <th className="text-left py-3 px-4">Listing</th>
                <th className="text-right py-3 px-2"><Eye className="w-3 h-3 inline" /> Views</th>
                <th className="text-right py-3 px-2"><TrendingUp className="w-3 h-3 inline" /> Unlocks</th>
                <th className="text-right py-3 px-2 hidden sm:table-cell">Conv.</th>
                <th className="text-right py-3 px-2">Revenue</th>
                <th className="text-right py-3 px-2 hidden md:table-cell"><Star className="w-3 h-3 inline" /> Rating</th>
                <th className="text-right py-3 px-4 hidden md:table-cell"><MessageCircle className="w-3 h-3 inline" /> Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b border-[rgba(217,180,114,0.08)] hover:bg-[rgba(217,180,114,0.04)]" data-testid={`analytics-row-${r.id}`}>
                  <td className="py-3 px-4">
                    <Link to={r.kind === "character" ? `/character/${r.id}` : `/work/${r.id}`} className="font-serif-display text-[#F7F3EC] hover:text-[#E6B860] transition-colors line-clamp-1">
                      {r.title}
                    </Link>
                    <div className="flex gap-2 mt-0.5">
                      <span className="text-[10px] font-mono-brass uppercase tracking-widest text-[#D9B472]">{r.kind}</span>
                      {r.moderation_status !== "approved" && (
                        <span className="text-[10px] font-mono-brass uppercase tracking-widest text-[#8C3A34]">{r.moderation_status}</span>
                      )}
                    </div>
                  </td>
                  <td className="text-right py-3 px-2 font-mono-brass text-[#F7F3EC]">{r.views.toLocaleString()}</td>
                  <td className="text-right py-3 px-2 font-mono-brass text-[#F7F3EC]">{r.purchases}</td>
                  <td className="text-right py-3 px-2 font-mono-brass text-[#A8A095] hidden sm:table-cell">{r.conversion_percent}%</td>
                  <td className="text-right py-3 px-2 font-mono-brass text-[#E6B860]">{fmtMoney(r.revenue_cents)}</td>
                  <td className="text-right py-3 px-2 font-mono-brass hidden md:table-cell">
                    {r.rating_count ? <span className="text-[#F7F3EC]">{r.avg_rating.toFixed(1)}★ <span className="text-[#6E675E] text-[10px]">({r.rating_count})</span></span> : <span className="text-[#6E675E]">—</span>}
                  </td>
                  <td className="text-right py-3 px-4 font-mono-brass text-[#F7F3EC] hidden md:table-cell">{r.reactions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function EmailPrefsPanel() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/me/email-prefs").then(({ data }) => setPrefs(data)).catch(() => setPrefs({ follower_digest: true }));
  }, []);
  if (!prefs) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      const next = !prefs.follower_digest;
      await api.put("/me/email-prefs", { follower_digest: next });
      setPrefs({ follower_digest: next });
      toast.success(next ? "Sunday digest turned on." : "Sunday digest turned off — inbox saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-14 gold-frame paper-scroll rounded-sm p-5 sm:p-6" data-testid="email-prefs-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-xl">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4 text-[#D9B472]" />
            <span className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass">Email preferences</span>
          </div>
          <h3 className="font-serif-display text-xl text-[#F7F3EC] mb-1">Sunday follower digest</h3>
          <p className="text-sm text-[#A8A095] font-serif-body italic">
            One roundup email each Sunday of every new fragment and character from every pen name you follow.
          </p>
        </div>
        <button
          onClick={toggle}
          disabled={busy}
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 ${prefs.follower_digest ? "bg-[#D9B472]" : "bg-[#3a342b]"} disabled:opacity-50`}
          data-testid="prefs-digest-toggle"
          role="switch"
          aria-checked={prefs.follower_digest}
          aria-label="Sunday follower digest"
        >
          <span className={`inline-block h-5 w-5 rounded-full bg-[#0D0C0A] transition-transform duration-200 ${prefs.follower_digest ? "translate-x-8" : "translate-x-1"}`} />
        </button>
      </div>
    </section>
  );
}
